// Anita Week 4 / Day 4 — Safe Route. Grade-3 fractions of 36 (1/3 and 1/4),
// time comparison with waiting, safe-vs-short logic and print-ready 4-section
// route table.

const day4 = {
  id: 'day-4',
  studentId: 'anita',
  weekId: 'week-4',
  dayId: 'day-4',
  status: 'active',
  title: 'Безопасный маршрут / Safe Route',
  shortTitle: 'Безопасный маршрут / Safe Route',
  theme:
    'Анита учится выбирать не только короткий, но и безопасный путь. / Anita learns to choose a safe route, not only the short one.',
  estimatedTimeLabel: '60–90 минут / 60–90 minutes',
  lessonTiming: { minMinutes: 60, maxMinutes: 90, breaks: 2 },
  bilingualMode: 'ru-primary-en-helper',
  courseLayers: ['reading', 'math', 'map', 'safety', 'logic', 'spelling', 'creative', 'speech'],
  readingLevel: 'grade-2',
  nimnyIntro:
    'Нимни рассказывает Аните, что не любой короткий путь — правильный. Иногда лучше пройти чуть дольше, но безопасно. Сегодня выбираем умный путь. / Nimny says a short path is not always the right path. Today we choose a smart route.',
  goals: [
    'Считать 1/3 и 1/4 от общего количества точек. / Find 1/3 and 1/4 of a total.',
    'Сравнивать полное время двух путей. / Compare full time of two paths.',
    'Читать таблицу маршрута из 4 участков. / Read a 4-section route table.',
    'Найти логический ответ: короткий или безопасный. / Answer: short vs safe.',
    'Написать простые русские слова безопасности. / Write simple safety words.'
  ],
  vocabulary: [
    { term: 'безопасный / safe', definition: 'без опасности. / free of danger.' },
    { term: 'опасный / dangerous', definition: 'с опасностью. / with danger.' },
    { term: 'взрослый / adult', definition: 'человек, который помогает. / a person who helps.' },
    { term: 'дорога / road', definition: 'место, где ходят или ездят. / place to walk or drive.' },
    { term: 'место / place', definition: 'точка на карте. / point on a map.' },
    { term: 'помощь / help', definition: 'когда взрослый рядом. / when an adult is near.' }
  ],
  onlineTasks: [
    {
      id: 'reading-words',
      type: 'text',
      title: 'Задание 1 — Слова безопасного маршрута / Safe Route Words',
      prompt: 'Прочитай по-русски:\nбезопасный\nопасный\nвзрослый\nдорога\nместо\nпомощь',
      ruPrimaryText: 'Прочитай 6 слов безопасности.',
      enHelpText: 'Read 6 safety words in Russian.',
      answer: 'Анита пробует читать русские слова.'
    },
    {
      id: 'reading-sentences',
      type: 'text',
      title: 'Задание 2 — Маленькое чтение / Short Reading',
      prompt:
        'Прочитай по-русски:\nБезопасный маршрут не всегда самый короткий.\nИногда лучше пройти немного дальше, но идти по знакомой дороге.\nНа карте можно отметить опасные места и безопасные точки.\nХорошая карта помогает выбрать умный путь.',
      ruPrimaryText: 'Прочитай 4 фразы про безопасный путь.',
      enHelpText: 'A safe route is not always the shortest. A good map shows safe and dangerous places.',
      answer: 'Анита пробует читать 4 фразы.'
    },
    {
      id: 'math-safe-points',
      type: 'text',
      title: 'Задание 3 — Безопасные точки / Safe Points',
      prompt:
        'На карте 36 точек.\n1/3 точек — безопасные места.\n1/4 точек — места, где нужна помощь взрослого.\nОстальные точки — обычные.\n\nОтветь:\n1. Сколько безопасных мест?\n2. Сколько мест «нужна помощь взрослого»?\n3. Сколько обычных точек на карте?',
      ruPrimaryText: 'Работа с дробями 1/3 и 1/4 от общего количества 36.',
      enHelpText: 'Find 1/3 of 36 and 1/4 of 36, then subtract both from 36.',
      answer: '1) 1/3 от 36 = 12. 2) 1/4 от 36 = 9. 3) 36 − 12 − 9 = 15 обычных точек.'
    },
    {
      id: 'math-compare-routes',
      type: 'text',
      title: 'Задание 4 — Сравнение маршрутов / Compare Routes',
      prompt:
        'Маршрут A: 6 клеток и все клетки безопасные.\nМаршрут B: 4 клетки, но 2 клетки идут через опасное место.\n\nОтветь:\n1. Какой маршрут длиннее?\n2. Какой маршрут лучше выбрать?\n3. Почему?',
      ruPrimaryText: 'Сравни короткий и безопасный маршруты.',
      enHelpText: 'Short is not always safe. Choose the safer route.',
      answer: '1) A длиннее (6 против 4). 2) Лучше A. 3) Он безопасный, у B — опасные клетки.'
    },
    {
      id: 'math-safe-time',
      type: 'text',
      title: 'Задание 5 — Время безопасного пути / Time of Safe Path',
      prompt:
        'Безопасный путь занимает 16 минут.\nКороткий путь занимает 9 минут, но нужно 8 минут ждать взрослого.\n\nОтветь:\n1. Полное время короткого пути?\n2. Какой путь быстрее?\n3. На сколько минут?',
      ruPrimaryText: 'Сравни полное время: идти + ждать.',
      enHelpText: 'Add waiting time to the short path first.',
      answer: '1) 9 + 8 = 17 минут. 2) Безопасный быстрее. 3) 17 − 16 = 1 минута.'
    },
    {
      id: 'math-route-table',
      type: 'text',
      title: 'Задание 6 — Таблица маршрута / Route Table',
      prompt:
        'Таблица:\nдом → дерево: 12 м\nдерево → мост: 18 м\nмост → лавочка: 15 м\nлавочка → домик Нимни: 20 м\n\nОтветь:\n1. Сколько метров весь маршрут?\n2. Первые два участка?\n3. Сколько осталось после первых двух?',
      ruPrimaryText: 'Чтение таблицы + остаток.',
      enHelpText: 'Sum the four sections; find first two; subtract.',
      answer: '1) 12 + 18 + 15 + 20 = 65. 2) 12 + 18 = 30. 3) 65 − 30 = 35 метров.'
    },
    {
      id: 'logic-safe-vs-short',
      type: 'text',
      title: 'Задание 7 — Логика: короткий или безопасный? / Short or Safe?',
      prompt:
        'Что важнее для детского маршрута: самый короткий путь или самый безопасный? Объясни 1–3 предложениями.',
      ruPrimaryText: 'Выбор между коротким и безопасным.',
      enHelpText: 'For a child, safety is more important than a couple of extra minutes.',
      answer: 'Ожидаемо: «Безопасный. Ребёнок должен идти там, где знакомо и где рядом взрослый.»'
    },
    {
      id: 'spelling-day4',
      type: 'text',
      title: 'Задание 8 — Правописание / Spelling',
      prompt:
        'Напиши:\nбезопасный\nопасный\nвзрослый\nдорога\nпомощь\n\nВставь буквы:\nбез_пасный\nо_асный\nвзр_слый\nдор_га\nпо_ощь',
      ruPrimaryText: 'Напиши 5 слов безопасности и вставь буквы.',
      enHelpText: 'Write 5 safety words and fill in the missing letters.',
      answer: 'безопасный, опасный, взрослый, дорога, помощь.'
    },
    {
      id: 'creative-safe-route',
      type: 'text',
      title: 'Задание 9 — Мой безопасный маршрут / My Safe Route',
      prompt:
        'Напиши 2 предложения про безопасный маршрут:\n«Мой безопасный маршрут — ______. Я иду с ______.»\nПиши только выдуманный маршрут, не настоящий адрес.',
      ruPrimaryText: 'Напиши 2 предложения про безопасный путь.',
      enHelpText: 'Write 2 short sentences about a safe route.',
      hint: 'Только выдуманный маршрут. / Fictional only.',
      answer: 'Open-ended. 2 связные фразы про безопасный путь.'
    },
    {
      id: 'speech-day4',
      type: 'text',
      title: 'Задание 10 — Объясни родителю / Explain to Parent',
      prompt:
        'Скажи родителю по-русски:\nБезопасный путь важнее короткого.\nЛучше идти со взрослым.\nЕсть безопасные и опасные места.',
      ruPrimaryText: 'Объясни выбор безопасного пути по-русски.',
      enHelpText: 'Tell the parent: safe is more important than short. Go with an adult.',
      answer: 'Accept if Anita expresses the safe-first idea in Russian.'
    }
  ],
  notebookInstructions: [
    'Если нет принтера:',
    '1. Начерти таблицу маршрута из 4 участков.',
    '2. Реши задачи с 1/3 и 1/4 от 36.',
    '3. Сделай сравнение времени.',
    '4. Напиши 2 предложения про безопасный маршрут.',
    '5. Расскажи родителю: безопасный > короткий.'
  ],
  printWorksheet: {
    title: 'Безопасный маршрут / Safe Route',
    subtitle: 'Неделя 4 · День 4 / Week 4 · Day 4',
    pages: [
      {
        title: 'Маршрут дня / Route',
        blocks: [
          { type: 'text', text: 'Имя: __________________________   Дата: __________________________' },
          { type: 'text', text: 'Миссия: Сегодня ты учишься выбирать безопасный путь, а не только короткий. / Today you learn to choose safety over shortcuts.' },
          { type: 'text', text: 'Задание 1 — Слова безопасного маршрута. Прочитай и обведи:' },
          { type: 'list', items: [
            'безопасный / safe',
            'опасный / dangerous',
            'взрослый / adult',
            'дорога / road',
            'место / place',
            'помощь / help'
          ] },
          { type: 'text', text: 'Задание 2 — Маленькое чтение. Поставь галочку после чтения:' },
          { type: 'list', items: [
            '□ Безопасный маршрут не всегда самый короткий.',
            '□ Иногда лучше пройти немного дальше, но идти по знакомой дороге.',
            '□ На карте можно отметить опасные места и безопасные точки.',
            '□ Хорошая карта помогает выбрать умный путь.'
          ] },
          { type: 'text', text: 'Задание 3 — Безопасные точки. Готовая таблица:' },
          { type: 'list', items: [
            'Всего точек                        | 36',
            'Безопасные места (1/3)             | 1/3 от 36 = ___',
            'Нужна помощь взрослого (1/4)       | 1/4 от 36 = ___',
            'Обычные точки                      | 36 − ___ − ___ = ___'
          ] },
          { type: 'text', text: 'Задание 4 — Сравнение маршрутов. Готовая схема:' },
          { type: 'list', items: [
            'Маршрут A: □ □ □ □ □ □  (все безопасные)',
            'Маршрут B: □ ⚠ □ ⚠     (короче, но есть опасные клетки)',
            'Длиннее: ___',
            'Выбор: ___',
            'Почему: ______________________________________________________'
          ] }
        ]
      },
      {
        title: 'Время и таблица маршрута / Time and Route Table',
        blocks: [
          { type: 'text', text: 'Задание 5 — Время безопасного пути. Готовая таблица времени (минуты идти + минуты ждать = всего минут). Математика: сравни полное время путей.' },
          { type: 'list', items: [
            'Путь        | Идти | Ждать | Всего',
            'Безопасный  |  16  |  0    |  ___',
            'Короткий    |   9  |  8    |  ___',
            'Быстрее: ___',
            'На сколько: ___'
          ] },
          { type: 'text', text: 'Задание 6 — Таблица маршрута. Готовая таблица из 4 участков:' },
          { type: 'list', items: [
            'Участок                    | Метры',
            'Дом → дерево               |  12',
            'Дерево → мост              |  18',
            'Мост → лавочка             |  15',
            'Лавочка → домик Нимни      |  20',
            'Всего: ___',
            'Первые два участка: ___',
            'Осталось: ___'
          ] }
        ]
      },
      {
        title: 'Логика, русский и речь / Logic, Russian, Speech',
        blocks: [
          { type: 'text', text: 'Задание 7 — Логика: короткий или безопасный. Ответь 1–3 предложения.' },
          { type: 'text', text: 'Ответ: __________________________________________________________________________________________________________________' },
          { type: 'text', text: 'Задание 8 — Правописание. Напиши и вставь буквы:' },
          { type: 'list', items: [
            'безопасный   опасный   взрослый   дорога   помощь',
            'без_пасный    о_асный    взр_слый    дор_га    по_ощь'
          ] },
          { type: 'text', text: 'Задание 9 — Мой безопасный маршрут. Нарисуй маленький рисунок-проект своего безопасного маршрута и напиши 2 предложения (только выдуманный маршрут):' },
          { type: 'list', items: [
            'Мой безопасный маршрут — __________________________________.',
            'Я иду с __________________________________.'
          ] },
          { type: 'text', text: 'Задание 10 — Объясни родителю. Скажи:' },
          { type: 'list', items: [
            '□ Безопасный путь важнее короткого.',
            '□ Лучше идти со взрослым.',
            '□ Есть безопасные и опасные места.'
          ] },
          { type: 'text', text: 'Parent Check: □ прочитала слова  □ прочитала 4 фразы  □ 1/3 и 1/4 от 36  □ сравнение маршрутов  □ таблица времени  □ таблица маршрута  □ логика  □ 2 предложения.' },
          { type: 'text', text: 'Notebook Mode fallback: если нет принтера, начерти таблицы времени и маршрута в тетради и реши задания 3–6.' }
        ]
      }
    ]
  },
  parentGuide: {
    goal: 'Анита работает с дробями 1/3 и 1/4 от одного целого, сравнивает пути по полному времени и обосновывает безопасный выбор.',
    mathGoal: 'U.S. Grade 3: 1/3 и 1/4 от 36; сумма 4 участков + первые два; полное время (идти + ждать).',
    russianGoal: 'Прочитать 6 слов безопасности и 4 фразы; написать 5 слов и вставить буквы.',
    readingWritingGoal: 'Логический ответ + 2 предложения про безопасный маршрут.',
    practicalGoal: 'Ясно сказать: «Безопасный путь важнее короткого.»',
    parentTheoryCard: {
      title: 'Как объяснить безопасный маршрут',
      purpose: 'Показать, что «короче» не значит «лучше», особенно для ребёнка.',
      keyMessage: 'Для ребёнка безопасность важнее пары минут.',
      parentScript: [
        '«1/3 от 36 = 12. 1/4 от 36 = 9. Осталось: 36 − 12 − 9 = 15.»',
        '«Полное время = идти + ждать. Смотри на общее время.»',
        '«Даже если путь длиннее, безопаснее — значит лучше.»',
        '«Опасные клетки на карте — не только рисунок. Их избегают.»'
      ],
      practiceSteps: [
        'Возьмите бумагу с 36 точками и обведите 12 как безопасные, 9 как «помощь взрослого».',
        'Посчитайте остаток: 15 обычных.',
        'В задании 5 заполните столбец «Всего» вместе.',
        'В задании 6 сложите таблицу маршрута.'
      ],
      doNotSay: [
        'Не решай задачи за Аниту.',
        'Не пропускай слово «полное» в столбце «Всего».',
        'Не описывай реальные опасные места города.'
      ],
      paperOnlyNotes: [
        'Обсуждение маршрутов — только на бумаге.',
        'Не проводи маршрут по настоящей карте города с ребёнком.'
      ]
    },
    whatThisLessonTeaches: 'Дроби от одного целого, чтение таблицы маршрута и логика безопасного выбора.',
    keyMessage: 'Безопасный путь — умный путь.',
    howToExplain: 'Скажите: «Смотри полное время и смотри, где опасные клетки. Выбирай безопаснее.»',
    exampleDialogue:
      'Parent: 1/3 от 36?\nChild: 12.\nParent: 1/4 от 36?\nChild: 9.\nParent: Обычных сколько?\nChild: 15.\nParent: Какой путь лучше — A или B?\nChild: A, потому что безопасный.',
    readingSupport: 'Слова «безопасный», «опасный», «взрослый» — по слогам.',
    russianSpeechGoal: 'Анита говорит: «Безопасный путь важнее короткого. Лучше идти со взрослым.»',
    mathAnswerKey:
      'math-safe-points: 1/3 от 36 = 12; 1/4 от 36 = 9; 36 − 12 − 9 = 15 обычных точек.\n' +
      'math-compare-routes: A длиннее (6 против 4); лучше A — безопасный.\n' +
      'math-safe-time: короткий = 9 + 8 = 17 минут; безопасный = 16; быстрее на 17 − 16 = 1 минуту.\n' +
      'math-route-table: 12 + 18 + 15 + 20 = 65; первые два = 12 + 18 = 30; осталось 65 − 30 = 35 метров.',
    spellingAnswerKey: 'безопасный\nопасный\nвзрослый\nдорога\nпомощь',
    answerKey: [
      { taskId: 'reading-words', answer: 'Попытка русского чтения 6 слов.' },
      { taskId: 'reading-sentences', answer: 'Попытка русского чтения 4 фраз.' },
      { taskId: 'math-safe-points', answer: '12; 9; 15.' },
      { taskId: 'math-compare-routes', answer: 'A длиннее; выбор A; A безопаснее.' },
      { taskId: 'math-safe-time', answer: 'короткий 17; безопасный 16; на 1 минуту.' },
      { taskId: 'math-route-table', answer: 'всего 65; первые два 30; осталось 35 метров.' },
      { taskId: 'logic-safe-vs-short', answer: 'Безопасный. Ребёнок должен быть в безопасности.' },
      { taskId: 'spelling-day4', answer: 'безопасный, опасный, взрослый, дорога, помощь.' },
      { taskId: 'creative-safe-route', answer: '2 предложения про безопасный маршрут.' },
      { taskId: 'speech-day4', answer: 'Идея «безопасный > короткий» по-русски.' }
    ],
    check: [
      'Анита посчитала 1/3 и 1/4 от 36.',
      'Анита сравнила маршруты A и B и выбрала безопасный.',
      'Анита сравнила полное время двух путей.',
      'Анита правильно прочитала таблицу маршрута.',
      'Анита написала 2 предложения про безопасный маршрут.',
      'Анита объяснила родителю: безопасный > короткий.'
    ],
    spellingCheck: 'Проверить безопасный, опасный, взрослый, дорога, помощь.',
    readingCheck: 'Главное — попытка чтения.',
    practicalCheck: 'Только выдуманные маршруты. Никаких реальных адресов.',
    drawingCriteria: 'Не требуется. Достаточно таблиц и 2 предложений.',
    commonMistakes: [
      '«Короче = быстрее.» Правильно: смотри полное время.',
      '«1/3 от 36 = 3.» Правильно: 1/3 от 36 = 12.',
      '«Безопасность не считается.» Правильно: для ребёнка безопасность — приоритет.'
    ],
    discussionPrompts: [
      'Что важнее для ребёнка: скорость или безопасность?',
      'Как отметить опасные места на карте?',
      'Кого можно взять в помощь по пути?'
    ],
    ifEasy: 'Добавить 3-й маршрут C: 5 клеток и 1 опасная клетка. Сравнить с A и B.',
    ifDifficult: 'Оставить только 3 и 4 задание; вместо 1/3 и 1/4 использовать «половину».',
    rewardGuidance: 'Базовая награда — 3 нима. Бонус за 2 предложения и обоснование выбора.',
    tokenReward: 'Нимы — это призовые токены курса, а не оплата.',
    parentOnlyNotes: 'Обсуждайте маршруты только на бумаге. Никаких реальных адресов вслух.',
    safetyNotes: 'Не описывай реальные опасные места города. Всё — выдуманные маршруты. Реальную дорогу обсуждают отдельно, вне урока, и только с родителем.'
  },
  reward: {
    unit: 'nim',
    unitName: 'ним',
    base: 3,
    max: 5,
    label: 'Награда за миссию',
    display: '3 нима',
    bonuses: [
      { id: 'safe-choice-explain', label: 'Бонус за ясное обоснование безопасного выбора', amount: 1 },
      { id: 'clear-fractions', label: 'Бонус за точные 1/3 и 1/4 от 36', amount: 1 }
    ]
  },
  completionRequirements: {
    requiredTaskIds: [
      'reading-words',
      'reading-sentences',
      'math-safe-points',
      'math-compare-routes',
      'math-safe-time',
      'math-route-table',
      'logic-safe-vs-short',
      'spelling-day4',
      'creative-safe-route',
      'speech-day4'
    ],
    allowNotebookCompletion: true
  },
  nextLessonId: 'anita-week-4-day-5'
};

export default day4;
